import React, {useContext, useMemo} from 'react';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import HomeIcon from '@mui/icons-material/Home';
import FireTruckIcon from '@mui/icons-material/FireTruck';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import PeopleIcon from '@mui/icons-material/People';
import VerifiedIcon from '@mui/icons-material/Verified';
import SchoolIcon from '@mui/icons-material/School';
import LocalLibraryIcon from '@mui/icons-material/LocalLibrary';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import ConstructionIcon from '@mui/icons-material/Construction';
import DescriptionIcon from '@mui/icons-material/Description';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import AssessmentIcon from '@mui/icons-material/Assessment';
import SettingsIcon from '@mui/icons-material/Settings';
import HelpOutlinedIcon from '@mui/icons-material/HelpOutlined';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import MoreTimeIcon from '@mui/icons-material/MoreTime';
import PunchClockIcon from '@mui/icons-material/PunchClock';
import ChecklistIcon from '@mui/icons-material/Checklist';
import EmergencyIcon from '@mui/icons-material/Emergency';
import HistoryIcon from '@mui/icons-material/History';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import BritishColumbiaIcon from '@embertracking/ui/components/BritishColumbiaIcon';
import GroupsIcon from '@mui/icons-material/Groups';
import ApartmentIcon from '@mui/icons-material/Apartment';
import FolderIcon from '@mui/icons-material/Folder';
import ForestIcon from '@mui/icons-material/Forest';
import MoveToInboxIcon from '@mui/icons-material/MoveToInbox';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';

import Badge from '@mui/material/Badge';

import {UserContext} from '-/contexts/User';
import {SettingsContext} from '-/contexts/Settings';
import {StationsContext} from '-/contexts/Stations';
import {FileCategoriesContext} from '-/contexts/FileCategories';
import {hasFeature} from '-/features';
import {hasPermission} from '-/data/utils';
import useAssignmentBadge from '-/hooks/useAssignmentBadge';
import useDeploymentBadge from '-/hooks/useDeploymentBadge';
import useAdminHoursBadge from '-/hooks/useAdminHoursBadge';
import useOnboardingRequests from '-/hooks/useOnboardingRequests';
import useRegionalCalendarAvailable from '-/hooks/useRegionalCalendarAvailable';
import DynamicIcon from '-/components/DynamicIcon';

// A menu icon with an optional count badge. `sx` is forwarded to the inner icon
// so the collapsed desktop rail (which clones item.icon with a sizing sx) keeps
// working; the Badge wraps the sized icon.
//
// `color` defaults to error because most badges here are a backlog — something
// the member is behind on. A count that's just live state (deployments out) is
// not a problem to fix, so it says so in a colour that isn't an alarm.
function BadgedIcon({icon, count, sx, color = 'error'}) {
    const inner = sx ? React.cloneElement(icon, {sx}) : icon;
    if (!count) return inner;
    // A single digit is a circle. MUI pads a badge `0 6px` regardless of what's
    // in it, which pushed a one-character count out to 18x16 — an oval, next to
    // a column of round icons. Dropping the padding lets minWidth and height
    // agree at 16; two or more digits keep the side padding and read as a pill.
    const single = count < 10;
    return (
        <Badge
            color={color}
            badgeContent={count}
            max={99}
            sx={{
                '& .MuiBadge-badge': {
                    fontSize: 10,
                    height: 16,
                    minWidth: 16,
                    fontWeight: 600,
                    padding: single ? 0 : '0 4px',
                    borderRadius: '999px',
                    // Sits 2px above the icon's top edge rather than centred on
                    // it, so it clears the glyph instead of eating into it.
                    top: -2
                }
            }}
        >
            {inner}
        </Badge>
    );
}

export default function useMenuItems() {
    const {currentUser = {}} = useContext(UserContext);
    const {fileCategories = []} = useContext(FileCategoriesContext);
    const {stations} = useContext(StationsContext);
    const assignmentCount = useAssignmentBadge();
    const deploymentCount = useDeploymentBadge();
    const {actionCount: adminHoursActionCount, timesheetCount: adminHoursTimesheetCount} = useAdminHoursBadge();
    // Counts what's waiting on whoever is looking: questions from Ember
    // Tracking for a department admin, department replies for the operator.
    const {enabled: onboardingEnabled, waiting: onboardingWaiting} = useOnboardingRequests();
    // Settings drive hasFeature(); depend on it so toggling a feature in
    // Settings re-renders the menu live (no refresh). App.js updates both the
    // SettingsContext and the hasFeature() backing store from one snapshot.
    const settings = useContext(SettingsContext);
    const regionalPermissions = ['read', 'request', 'manage'].map(action => hasPermission(currentUser, `regionalCalendar.${action}`));
    const canBrowseRegional = Boolean(hasFeature('calendar') && settings.hub?.projectId && !currentUser.isStation && regionalPermissions.some(Boolean));
    const regionalAvailable = useRegionalCalendarAvailable(canBrowseRegional && Boolean(currentUser.uid), `${settings.hub?.projectId}:${currentUser.uid}:${regionalPermissions.join(':')}`);

    return useMemo(() => {
        const items = [];

        items.push({id: '/', label: 'Dashboard', to: '/', icon: <HomeIcon />, group: 'operations'});

        // Onboarding requests (docs/architecture/onboarding-requests.md). Right
        // under Dashboard because while it's switched on, setting the
        // department up IS the job; the operator switches it off afterwards.
        if (onboardingEnabled) {
            items.push({id: '/onboarding', label: 'Onboarding', to: '/onboarding', icon: <BadgedIcon icon={<MoveToInboxIcon />} count={onboardingWaiting} color="warning" />, group: 'operations'});
        }

        items.push({id: '/stations', label: stations?.length === 1 ? 'Station' : 'Stations', to: '/stations', icon: <WarehouseIcon />, group: 'operations'});

        if (hasPermission(currentUser, 'apparatus.read')) {
            items.push({id: '/apparatus', label: 'Apparatus', to: '/apparatus', icon: <FireTruckIcon />, group: 'operations'});
        }

        if (hasPermission(currentUser, 'incidents.read')) {
            items.push({id: '/incidents', label: 'Incidents', to: '/incidents', icon: <WhatshotIcon />, group: 'operations'});
        }

        if (hasPermission(currentUser, 'reports.read')) {
            items.push({id: '/reports', label: 'Reports', to: '/reports', icon: <AssessmentIcon />, group: 'operations'});
        }

        if (hasFeature('ask') && hasPermission(currentUser, 'reports.read')) {
            items.push({id: '/ask', label: 'Ask', to: '/ask', icon: <AutoAwesomeIcon />, group: 'operations'});
        }

        const canSeeCalendar = hasFeature('calendar') && (
            (hasPermission(currentUser, 'training.stats') || hasPermission(currentUser, 'training.read')) ||
            hasPermission(currentUser, 'incidents.read') ||
            (hasFeature('dutyShifts') && hasPermission(currentUser, 'dutyShifts.read')));

        if (canSeeCalendar) {
            items.push({id: '/calendar', label: 'Calendar', to: '/calendar', icon: <CalendarMonthIcon />, group: 'scheduling'});
        }

        if (canBrowseRegional && regionalAvailable) {
            items.push({id: '/regional-training', label: 'Regional Calendar', to: '/regional-training', icon: <BritishColumbiaIcon />, group: 'scheduling'});
        }

        if (hasPermission(currentUser, 'training.stats') || hasPermission(currentUser, 'training.write')) {
            if (hasFeature('qualificationMatrix')) {
                items.push({id: '/matrix/qualification', label: 'Qualification Matrix', to: '/matrix/qualification', icon: <VerifiedIcon />, group: 'training'});
            }
            items.push({id: '/training', label: 'Training Records', to: '/training', icon: <FactCheckIcon />, group: 'training'});

            if (hasPermission(currentUser, 'training.write')) {
                items.push({id: '/matrix/training', label: 'Training Matrix', to: '/matrix/training', icon: <SchoolIcon />, group: 'training'});
            }
        }

        if (hasFeature('lessonPlans') && hasPermission(currentUser, 'lessonplans.read')) {
            items.push({id: '/lessonplans', label: 'Lesson Plans', to: '/lessonplans', icon: <MenuBookIcon />, group: 'training'});
        }

        if (hasFeature('requirements') && (hasPermission(currentUser, 'requirements.read') || hasPermission(currentUser, 'requirements.write'))) {
            items.push({id: '/requirements', label: 'Requirements', to: '/requirements', icon: <LocalLibraryIcon />, group: 'training'});
        }

        // The recruit skill-drill sections appear as rows inside the Requirements grid
        // (/requirements), not as their own nav items — see pages/Requirements.js.

        // Member-facing: any logged-in member can have assignments — but a station
        // (shared apparatus login) has no individual member identity, so it doesn't.
        if (hasFeature('requirements') && !currentUser.isStation) {
            items.push({id: '/assignments', label: 'Assignments', to: '/assignments', icon: <BadgedIcon icon={<AssignmentTurnedInIcon />} count={assignmentCount} />, group: 'training'});
        }

        if (hasFeature('ohs') && hasPermission(currentUser, 'ohs.read')) {
            items.push({id: '/ohs', label: 'OH&S Meetings', to: '/ohs', icon: <HealthAndSafetyIcon />, group: 'training'});
        }

        if (hasFeature('publicEducation') && hasPermission(currentUser, 'publicEducation.read')) {
            items.push({id: '/public-education', label: 'Public Education', to: '/public-education', icon: <GroupsIcon />, group: 'training'});
        }

        if (hasPermission(currentUser, 'gear.read')) {
            items.push({id: '/gear', label: 'Gear', to: '/gear', icon: <ConstructionIcon />, group: 'resources'});
        }

        if (hasFeature('fuel') && hasPermission(currentUser, 'fuel.read')) {
            items.push({id: '/fuel', label: 'Fuel', to: '/fuel', icon: <LocalGasStationIcon />, group: 'resources'});
        }

        if (hasFeature('preplans') && hasPermission(currentUser, 'preplans.read')) {
            items.push({id: '/preplans', label: 'Preplans', to: '/preplans', icon: <ApartmentIcon />, group: 'resources'});
        }

        if (hasFeature('burnPermits') && hasPermission(currentUser, 'burnPermits.read')) {
            items.push({id: '/burn-permits', label: 'Burn Permits', to: '/burn-permits', icon: <LocalFireDepartmentIcon />, group: 'resources'});
        }

        if (hasFeature('deployments') && hasPermission(currentUser, 'deployments.read')) {
            items.push({
                id: '/deployments',
                label: 'Deployments',
                to: '/deployments',
                // Green, not red: apparatus out on a fire is the department
                // working, not a backlog someone is behind on.
                icon: <BadgedIcon icon={<ForestIcon />} count={deploymentCount} color="success" />,
                group: 'operations'
            });
        }

        if (hasFeature('dutyShifts') && hasPermission(currentUser, 'dutyShifts.read')) {
            items.push({id: '/dutyshifts', label: 'Duty Shifts', to: '/dutyshifts', icon: <EventAvailableIcon />, group: 'scheduling'});
        }

        if (hasFeature('payroll') && hasPermission(currentUser, 'payroll.read') && hasPermission(currentUser, 'incidents.read')) {
            items.push({id: '/payroll', label: 'Payroll', to: '/payroll', icon: <AccountBalanceIcon />, group: 'scheduling'});
        }

        // Member-facing Admin Hours (#1206). Self-service: any member who can
        // submit hours sees it, whether or not they have a timesheet yet — the
        // page's "New Timesheet" button is the entry point. The badge counts sheets
        // still waiting on the member (draft/rejected). Payroll admins also get it
        // (#1376) — it's where they log hours on a member's behalf.
        //
        // A member who holds NEITHER permission still gets the item once a sheet
        // exists in their name. payroll.submitHours is off by default, so an
        // officer logging hours on someone's behalf (#1376) — or putting them on a
        // shared roster (#1441) — otherwise produced a sheet the member had no way
        // to open, while the "logged for you" email deep-linked them straight to it.
        if (!currentUser.isStation && (
            hasPermission(currentUser, 'payroll.submitHours') ||
            hasPermission(currentUser, 'payroll.write') ||
            adminHoursTimesheetCount > 0
        )) {
            items.push({id: '/admin-hours', label: 'Admin Hours', to: '/admin-hours', icon: <BadgedIcon icon={<MoreTimeIcon />} count={adminHoursActionCount} />, group: 'scheduling'});
        }

        // JRFD members key their own hours into RDCO's Workzoom app; this page
        // lists theirs in the shape the app asks for. Every member, no
        // permission — it's their own pay — but never a station login.
        if (hasFeature('workzoom') && !currentUser.isStation) {
            items.push({id: '/workzoom', label: 'Workzoom', to: '/workzoom', icon: <PunchClockIcon />, group: 'scheduling'});
        }

        // Member-facing, like Assignments above: /tasks is scoped to the viewer
        // unless they hold tasks.read (which adds the department-wide tabs), and
        // it now also carries their requirement assignments and anything awaiting
        // their review. The daily digest deep-links every member here ("View all
        // tasks"), so gating the nav item on `admin` left everyone else on a page
        // they could reach from their inbox but never find again. Stations are a
        // shared apparatus login with no individual member identity — nothing on
        // this page is theirs.
        if (!currentUser.isStation) {
            items.push({id: '/tasks', label: 'Tasks', to: '/tasks', icon: <ChecklistIcon />, group: 'tasks'});
        }

        if (hasFeature('trainingStandards') && hasPermission(currentUser, 'nfpa.read')) {
            items.push({id: '/training-standards', label: 'Training Standards', to: '/training-standards', icon: <DescriptionIcon />, group: 'standards'});
        }

        if (hasFeature('quizzes')) {
            items.push({id: '/quizzes/fr', label: 'First Responder Quiz', to: '/quizzes/fr', icon: <EmergencyIcon />, group: 'standards'});
        }

        if (hasFeature('files') && hasPermission(currentUser, 'files.read') && fileCategories.length > 0) {
            fileCategories.forEach(cat => {
                const path = `/files/${cat.slug || cat.id}`;
                items.push({id: path, label: cat.name, to: path, icon: cat.icon ? <DynamicIcon name={cat.icon} /> : <FolderIcon />, group: 'files'});
            });
        }

        if (hasPermission(currentUser, 'users.write')) {
            items.push({id: '/users', label: 'Users', to: '/users', icon: <PeopleIcon />, group: 'administration'});
        }

        if (hasPermission(currentUser, 'admin')) {
            items.push({id: '/history', label: 'History', to: '/history', icon: <HistoryIcon />, group: 'administration'});
        }

        if (hasPermission(currentUser, 'admin')) {
            items.push({id: '/settings', label: 'Settings', to: '/settings', icon: <SettingsIcon />, group: 'administration'});
        }

        items.push({
            id: '/help',
            label: 'Help Center',
            to: '/help',
            icon: <HelpOutlinedIcon />,
            group: 'administration',
            keywords: ['contact', 'support', 'assistance', 'guides', 'documentation', 'faq']
        });

        return items;
        // Settings also backs hasFeature(), so recompute when a feature changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentUser, stations, fileCategories, settings, assignmentCount, deploymentCount, adminHoursActionCount, adminHoursTimesheetCount, onboardingEnabled, onboardingWaiting, canBrowseRegional, regionalAvailable]);
}
